// ARCState -- the ARC character expressing a REAL state of Martian's WORK
// layer (preparing, waiting for approval, done). It is the second approved
// character in the locked asset system and it has one job on a page: the
// operational / prepared-work story. GLIM stays the face and the intelligence.
//
// * Same 15 approved states and the same product-state engine as GLIM
//   (productStates.js); unknown values fall back to "neutral".
// * Critical states always carry a text label (requiresText).
// * Never rendered below ARC_MIN_PX, never as an icon / avatar, and never at
//   equal visual weight with GLIM in the same frame (one lead per frame).

import { ARC_MIN_PX, ARC_STATE_FILES } from './assets';
import { glimStateMeta, isGlimState } from './productStates';
import './brand.css';

const SIGNAL_DOT_STATES = new Set(['approval_required', 'warning', 'error', 'something_changed']);

export function ARCState({
  state = 'neutral',
  size = 96,
  showLabel,
  label,
  description,
  layout = 'inline',
  tone = 'light',
  motion = true,
  announce = true,
  className = '',
}) {
  if (size < ARC_MIN_PX) return null;
  const id = isGlimState(state) ? state : 'neutral';
  const meta = glimStateMeta(id);
  const text = label || meta.label;
  const visibleLabel = showLabel === undefined ? meta.requiresText : showLabel;
  const motionClass = motion ? `glim-motion-${meta.motion}` : '';

  return (
    <span
      className={[
        'glim-state',
        'arc-state',
        layout === 'stacked' ? 'glim-state--stacked' : '',
        tone === 'dark' ? 'glim-state--dark' : '',
        className,
      ].filter(Boolean).join(' ')}
      data-testid="arc-state"
      data-state={id}
      role={announce ? 'status' : undefined}
    >
      <img
        key={id}
        src={ARC_STATE_FILES[id]}
        width={size}
        height={size}
        alt=""
        aria-hidden="true"
        draggable={false}
        className={`arc-mark ${motionClass}`.trim()}
      />
      {visibleLabel ? (
        <span className="glim-state__label" data-testid="arc-state-label">
          {SIGNAL_DOT_STATES.has(id) && <span className="glim-state__signal-dot" aria-hidden="true" />}
          {text}
        </span>
      ) : (
        <span className="sr-only" aria-hidden={announce ? undefined : 'true'}>{description || meta.description}</span>
      )}
    </span>
  );
}

export default ARCState;
